/**
 * 装配体配合控制器求解输入服务。
 *
 * 文件职责：
 * 1. 创建新建/编辑 Mate 的预览求解输入；
 * 2. 在组件拖动或旋转后重求解已提交 Mate；
 * 3. 将求解诊断写回 Mate 文档。
 */

import * as THREE from 'three/webgpu';
import type { Object3D } from 'three/webgpu';
import type { Viewer } from '../../viewer/Viewer';
import {
  areComponentTransformSnapshotsEqual,
  applyComponentInstanceSnapshotToObject,
  createComponentInstanceSnapshotFromObject,
  type ComponentInstanceSnapshot,
} from '../component-instance';
import {
  setAssemblyMateSolveStatus,
  type AssemblyMateDocumentModel,
} from '../document';
import {
  MateSolveStatus,
  type AssemblyMateDefinition,
} from '../definition';
import {
  createAssemblyMatePreview,
  type AssemblyMatePreviewResult,
} from '../preview';
import {
  captureStandardMateSolverUnsolvedResult,
  resolveStandardMateComponentRotationAxis,
  resolveStandardMateFloatingRigidTransformGroup,
  StandardMateDistanceBranchPolicy,
  type StandardMateComponentRotationAxis,
  type StandardMateFloatingRigidTransformGroup,
  type StandardMateDrivenComponentConstraint,
  type AssemblyMateSolverComponentState,
  type AssemblyMateSolverWorldBounds,
  type StandardMateSolverInput,
} from '../solver';
import type {
  AssemblyMateCommittedSolveResult,
  AssemblyMateDocumentChangeContext,
} from './AssemblyMateControllerTypes';
import { readAssemblyMateObjectAssemblyNodeId } from './support/AssemblyMateObjectMetadata';
import { cloneAssemblyMateComponentInstanceSnapshot } from './support/AssemblyMateControllerStateHelpers';
import { resolveAssemblyMateComponentInstanceIds } from '../utils';
import {
  areAssemblyNodeIdentityEquivalent,
  collectAssemblyNodeIdentityLookupIds,
} from '../utils/AssemblyNodePath';
import type { AssemblyMateControllerRuntime } from './AssemblyMateControllerRuntime';

/** Mate 文档读取函数。 */
type ResolveMateDocument = () => AssemblyMateDocumentModel;

/** Mate 文档写回函数。 */
type CommitMateDocument = (
  document: AssemblyMateDocumentModel,
  context: AssemblyMateDocumentChangeContext,
) => void;

/** 已提交 Mate 求解邻接索引。 */
interface CommittedMateAdjacencyIndex {
  /** 索引对应的 Mate 文档对象。 */
  readonly document: AssemblyMateDocumentModel;
  /** 可参与实时求解的 Mate。 */
  readonly mateById: ReadonlyMap<string, AssemblyMateDefinition>;
  /** 组件 ID 到相关 Mate ID 的邻接表。 */
  readonly mateIdsByComponentId: ReadonlyMap<string, readonly string[]>;
}

/**
 * 已提交 Mate 局部重求解输入选项。
 */
interface CommittedSolverInputOptions {
  /** 本次交互对 driven 组件保留哪些位姿分量。 */
  readonly drivenComponentConstraints?: StandardMateSolverInput['drivenComponentConstraints'];
  /**
   * PropertyManager 当前可解但尚未提交的草稿 Mate。
   *
   * 设计说明：
   * SOLIDWORKS 在 Mate 预览阶段已经把草稿作为临时约束参与自由度限制；
   * 这里把它放入与已提交 Mate 相同的局部求解子图，而不是在拖动层额外拦截鼠标。
   */
  readonly transientMateDefinition?: AssemblyMateDefinition | null;
  /**
   * 是否把瞬态草稿配合扩展到“本次拖拽会联动到的已提交范围”。
   *
   * 设计说明：
   * 拖拽会移动“锚点 + 经已提交配合联动的整片零件”。瞬态预览配合只要有一侧（通常是已装配侧）落在这片
   * 会被移动的范围内——无论它是被直接抓取，还是作为已装配侧被联动带动——它的自由 follower 就必须在本次
   * 重求解里跟随，否则预览态下拖拽已装配侧时两个选中面会脱离同轴心/重合。仅拖拽重求解开启此扩展；能力探测
   * （旋转轴 / 浮动刚性组）保持“仅锚点”语义，以维持预览态孤立零件的自由度判定隔离。
   */
  readonly expandTransientToCommittedScope?: boolean;
}

/**
 * 新建或编辑 Mate 的预览求解输入选项。
 */
interface PreviewSolverInputOptions {
  /**
   * 是否允许临时 Mate 使用初始姿态增强。
   *
   * 设计说明：
   * 首次选择第二个实体后需要自动贴边；但用户已经在预览约束下拖动过零件、
   * 或点击确认只是在持久化当前位姿时，不能再次执行初始贴边。
   */
  readonly useTransientInitialPlacement?: boolean;
}

/**
 * 将世界坐标向量转换成求解器可序列化三元组。
 *
 * @param value 世界坐标向量。
 * @returns 三元组。
 */
function tupleFromVector3(value: THREE.Vector3): readonly [number, number, number] {
  return [value.x, value.y, value.z] as const;
}

/**
 * 解析预览阶段可用的组件世界包围盒。
 *
 * 设计说明：
 * Box3.setFromObject 需要遍历对象子树，不能放入已提交 Mate 的拖拽热路径。
 * 当前函数只被新建/编辑 Mate 预览输入调用，用于给求解器提供初始姿态避让所需的纯数据。
 *
 * @param object 组件 transform 根对象。
 * @returns 世界包围盒；空对象返回 null。
 */
function resolvePreviewComponentWorldBounds(object: Object3D): AssemblyMateSolverWorldBounds | null {
  const bounds = new THREE.Box3().setFromObject(object);
  if (bounds.isEmpty()) {
    return null;
  }

  return {
    min: tupleFromVector3(bounds.min),
    max: tupleFromVector3(bounds.max),
  };
}

/**
 * 装配体配合控制器求解输入服务选项。
 */
export interface AssemblyMateControllerSolveServiceOptions {
  /** Viewer 实例。 */
  readonly viewer: Viewer;
  /** 运行时服务。 */
  readonly runtime: AssemblyMateControllerRuntime;
  /** Mate 文档读取函数。 */
  readonly resolveMateDocument: ResolveMateDocument;
  /** Mate 文档写回函数。 */
  readonly commitMateDocument: CommitMateDocument;
}

/**
 * 装配体配合控制器求解输入服务。
 */
export class AssemblyMateControllerSolveService {
  private readonly viewer: Viewer;
  private readonly runtime: AssemblyMateControllerRuntime;
  private readonly resolveMateDocument: ResolveMateDocument;
  private readonly commitMateDocument: CommitMateDocument;
  private committedMateAdjacencyIndex: CommittedMateAdjacencyIndex | null = null;

  /**
   * 构造函数。
   *
   * @param options 服务选项。
   */
  public constructor(options: AssemblyMateControllerSolveServiceOptions) {
    this.viewer = options.viewer;
    this.runtime = options.runtime;
    this.resolveMateDocument = options.resolveMateDocument;
    this.commitMateDocument = options.commitMateDocument;
  }

  /**
   * 创建新建或编辑 Mate 的预览求解输入。
   *
   * @param definition Mate Definition。
   * @param options 预览求解输入选项。
   * @returns 求解器输入；缺少组件时返回 null。
   */
  public createPreviewSolverInput(
    definition: AssemblyMateDefinition,
    options: PreviewSolverInputOptions = {},
  ): StandardMateSolverInput | null {
    const firstComponent = this.resolvePreviewComponentState(definition.firstEntity.componentInstanceId);
    const secondComponent = this.resolvePreviewComponentState(definition.secondEntity.componentInstanceId);
    if (!firstComponent || !secondComponent) {
      return null;
    }

    const anchorComponentIds = new Set([
      definition.firstEntity.componentInstanceId,
      definition.secondEntity.componentInstanceId,
    ]);
    const scopedMates = this.resolveCommittedMateScope(anchorComponentIds)
      .filter((mate) => mate.mateId !== definition.mateId);
    const mateDefinitions = [...scopedMates, definition];
    const componentIds = new Set<string>([
      firstComponent.componentInstanceId,
      secondComponent.componentInstanceId,
    ]);
    mateDefinitions.forEach((mate) => {
      resolveAssemblyMateComponentInstanceIds(mate).forEach((componentInstanceId) => {
        componentIds.add(componentInstanceId);
      });
    });

    return {
      components: Array.from(componentIds).reduce<Record<string, AssemblyMateSolverComponentState>>(
        (components, componentInstanceId) => {
          const component = this.resolvePreviewComponentState(componentInstanceId);
          /**
           * 这里保留缺失组件的 Mate Definition，让独立求解器输出 missingReference 诊断，
           * 而不是由控制器提前吞掉上下文。
           */
          return component
            ? {
                ...components,
                [component.componentInstanceId]: component,
              }
            : components;
        },
        {},
      ),
      mateDefinitions,
      transientMateIds: [definition.mateId],
      activeMateIds: [definition.mateId],
      previewAnchorComponentInstanceIds: [definition.firstEntity.componentInstanceId],
      useTransientInitialPlacement: options.useTransientInitialPlacement ?? true,
    };
  }

  /**
   * 解析预览求解组件状态。
   *
   * @param componentInstanceId 组件实例 ID。
   * @returns 带预览包围盒的组件状态；组件缺失时返回 null。
   */
  private resolvePreviewComponentState(componentInstanceId: string): AssemblyMateSolverComponentState | null {
    const component = this.runtime.resolveComponentState(componentInstanceId);
    if (!component) {
      return null;
    }

    const object = this.runtime.resolveComponentObject(componentInstanceId);
    const worldBounds = object ? resolvePreviewComponentWorldBounds(object) : null;
    return worldBounds
      ? {
          ...component,
          worldBounds,
        }
      : component;
  }

  /**
   * 在组件移动或旋转后重求解已提交配合。
   *
   * @param input 重求解输入。
   * @returns 重求解结果；无相关 Mate 时返回 `null`。
   */
  public solveAfterComponentTransform(input: {
    readonly anchorComponentInstanceId: string;
    readonly updateDocumentStatus?: boolean;
    readonly drivenComponentConstraints?: StandardMateSolverInput['drivenComponentConstraints'];
    readonly transientMateDefinition?: AssemblyMateDefinition | null;
    /**
     * 求解失败自动采集的业务出口标识；传 `null` 表示本次调用属于探测性求解（如拖拽能力探测），
     * 失败是预期行为、不进入采集缓存。缺省按拖拽/旋转重求解出口采集。
     */
    readonly unsolvedCaptureContext?: string | null;
  }): AssemblyMateCommittedSolveResult | null {
    const anchorComponentIds = this.resolveComponentInstanceIdAliases(input.anchorComponentInstanceId);
    const solverInput = this.createCommittedSolverInput(anchorComponentIds, {
      drivenComponentConstraints: this.resolveDrivenComponentConstraintsForAliases(
        anchorComponentIds,
        input.drivenComponentConstraints,
      ),
      transientMateDefinition: input.transientMateDefinition ?? null,
      // 拖拽重求解：瞬态草稿配合的已装配侧只要被本次拖拽联动带动，就必须让其自由 follower 跟随。
      expandTransientToCommittedScope: true,
    });
    if (!solverInput) {
      return null;
    }

    return this.solveCommittedSolverInput(solverInput, {
      updateDocumentStatus: input.updateDocumentStatus,
      unsolvedCaptureContext: input.unsolvedCaptureContext === undefined
        ? 'committed-transform'
        : input.unsolvedCaptureContext,
    });
  }

  /**
   * 在持久化 Mate 文档恢复完成后求解当前所有已提交配合。
   *
   * 设计说明：
   * 1. 文档恢复只负责把保存的 Mate 定义和组件快照写回控制器，不代表当前装载模型已经满足约束；
   * 2. 子装配版本切换会先挂载新几何，再恢复 Mate 文档，此时必须由 cad-core 主动收敛一次已提交 Mate；
   * 3. 该入口不伪造用户拖动组件，因此不传入 driven 组件，求解计划会按 fixed / fallback 规则选择稳定锚点。
   *
   * @param input 恢复后求解选项。
   * @returns 求解结果；没有可用 Mate 时返回 `null`。
   */
  public solveAfterDocumentRestore(
    input: PuppyCAD.AssemblyMateDocumentRestoreSolveOptions = {},
  ): AssemblyMateCommittedSolveResult | null {
    const solverInput = this.createDocumentRestoreSolverInput();
    if (!solverInput) {
      return null;
    }

    const writableComponentLookupIds = input.writableComponentInstanceIds
      ? new Set(input.writableComponentInstanceIds.flatMap((componentInstanceId) => (
          collectAssemblyNodeIdentityLookupIds(componentInstanceId)
        )))
      : undefined;
    return this.solveCommittedSolverInput(solverInput, {
      updateDocumentStatus: input.updateDocumentStatus,
      unsolvedCaptureContext: 'document-restore',
      writableComponentLookupIds,
    });
  }

  /**
   * 执行已提交 Mate 求解并把结果应用回当前场景。
   *
   * @param solverInput 已提交 Mate 求解输入。
   * @param options 求解写回选项；`unsolvedCaptureContext` 非空时对非 Solved 结果做失败现场采集。
   * @returns 求解结果。
   */
  private solveCommittedSolverInput(
    solverInput: StandardMateSolverInput,
    options: {
      readonly updateDocumentStatus?: boolean;
      readonly unsolvedCaptureContext?: string | null;
      /** 恢复局部子树时允许写回位姿的完整 occurrence lookup 集合。 */
      readonly writableComponentLookupIds?: ReadonlySet<string>;
    } = {},
  ): AssemblyMateCommittedSolveResult {
    const result = createAssemblyMatePreview(solverInput);
    if (options.unsolvedCaptureContext && result.status !== MateSolveStatus.Solved) {
      captureStandardMateSolverUnsolvedResult({
        context: options.unsolvedCaptureContext,
        solverInput,
        status: result.status,
        diagnostics: result.diagnostics,
      });
    }
    const changedBeforeSnapshots: ComponentInstanceSnapshot[] = [];
    const changedAfterSnapshots: ComponentInstanceSnapshot[] = [];
    const changedObjects: Object3D[] = [];

    if (result.status === MateSolveStatus.Solved) {
      Object.entries(result.componentTransforms).forEach(([componentInstanceId, transform]) => {
        /**
         * 局部子树恢复仍需在完整 Mate 图上求解，但只允许事务范围内的 occurrence 落地位姿。
         * 这里仅展开同一完整身份的编码/解码形式，不采用 instance-id 或路径后缀匹配，
         * 避免不同归档包内合法同名节点被误判为同一组件。
         */
        if (
          options.writableComponentLookupIds
          && !collectAssemblyNodeIdentityLookupIds(componentInstanceId).some((lookupId) => (
            options.writableComponentLookupIds?.has(lookupId)
          ))
        ) {
          return;
        }
        const object = this.runtime.resolveComponentObject(componentInstanceId);
        if (!object) {
          return;
        }

        const beforeSnapshot = createComponentInstanceSnapshotFromObject({
          object,
          componentInstanceId,
        });
        if (areComponentTransformSnapshotsEqual(beforeSnapshot.transform, transform)) {
          return;
        }

        applyComponentInstanceSnapshotToObject(object as never, {
          ...beforeSnapshot,
          transform,
        });
        changedObjects.push(object);
        changedBeforeSnapshots.push(cloneAssemblyMateComponentInstanceSnapshot(beforeSnapshot));
        changedAfterSnapshots.push(createComponentInstanceSnapshotFromObject({
          object,
          assemblyId: beforeSnapshot.assemblyId,
          componentInstanceId: beforeSnapshot.componentInstanceId,
          sourceNodeId: beforeSnapshot.sourceNodeId,
        }));
      });

      if (changedAfterSnapshots.length > 0) {
        this.runtime.synchronizeComponentTransformEffects(changedObjects);
      }
    }

    const transientMateIds = new Set(solverInput.transientMateIds ?? []);
    if (options.updateDocumentStatus ?? this.shouldWriteCommittedSolveDiagnostics(result, transientMateIds)) {
      this.applyCommittedSolveDiagnostics(result, transientMateIds);
    }

    return {
      status: result.status,
      componentTransforms: result.componentTransforms,
      diagnostics: result.diagnostics,
      beforeSnapshots: changedBeforeSnapshots,
      afterSnapshots: changedAfterSnapshots,
    };
  }

  /**
   * 将外部传入的 driven 约束扩展到当前交互组件的所有运行时别名。
   *
   * @param componentAliases 当前交互组件别名集合。
   * @param constraints 原始 driven 约束。
   * @returns 可直接传给求解器输入构建流程的约束映射。
   */
  private resolveDrivenComponentConstraintsForAliases(
    componentAliases: ReadonlySet<string>,
    constraints: StandardMateSolverInput['drivenComponentConstraints'] | undefined,
  ): StandardMateSolverInput['drivenComponentConstraints'] | undefined {
    if (!constraints) {
      return undefined;
    }
    const directConstraint = [...componentAliases]
      .map((componentInstanceId) => constraints[componentInstanceId])
      .find((constraint) => Boolean(constraint));
    if (!directConstraint) {
      return constraints;
    }

    const expandedConstraints: Record<string, StandardMateDrivenComponentConstraint> = {
      ...constraints,
    };
    componentAliases.forEach((componentInstanceId) => {
      expandedConstraints[componentInstanceId] = constraints[componentInstanceId] ?? directConstraint;
    });
    return expandedConstraints;
  }

  /**
   * 解析组件在已提交 Mate 图中可用于“旋转零部件”命令的剩余旋转轴。
   *
   * 设计说明：
   * 命令层只能消费旋转轴 plain data，不能自行判断 Concentric + Coincident 是否构成转动副。
   * 这里复用与拖动重求解相同的已提交 Mate 子图和别名解析，确保选中可见 mesh、FeatureFace
   * 或真实组件根时都落到同一套 cad-core 配合图语义。
   *
   * @param input 组件旋转自由度解析输入。
   * @returns 可用旋转轴；当前组件没有明确剩余旋转自由度时返回 null。
   */
  public resolveComponentRotationAxis(input: {
    readonly componentInstanceId: string;
    readonly transientMateDefinition?: AssemblyMateDefinition | null;
  }): StandardMateComponentRotationAxis | null {
    const componentAliases = this.resolveComponentInstanceIdAliases(input.componentInstanceId);
    const solverInput = this.createCommittedSolverInput(componentAliases, {
      transientMateDefinition: input.transientMateDefinition ?? null,
    });
    if (!solverInput) {
      return null;
    }

    for (const componentInstanceId of componentAliases) {
      if (!solverInput.components[componentInstanceId]) {
        continue;
      }
      const rotationAxis = resolveStandardMateComponentRotationAxis(solverInput, componentInstanceId);
      if (rotationAxis) {
        return rotationAxis;
      }
    }

    return null;
  }

  /**
   * 解析组件所在的全浮动刚性 Mate 组合。
   *
   * 设计说明：
   * 该入口和剩余旋转轴解析共用 committed mate scope 与运行时别名规则。这样点击 mesh、拓扑代理或组件根时，
   * 都不会因为入口 ID 不同而得到不同的联动组。
   *
   * @param input 组件刚性组合解析输入。
   * @returns 全浮动刚性组合；不存在时返回 `null`。
   */
  public resolveFloatingRigidTransformGroup(input: {
    readonly componentInstanceId: string;
    readonly transientMateDefinition?: AssemblyMateDefinition | null;
  }): StandardMateFloatingRigidTransformGroup | null {
    const componentAliases = this.resolveComponentInstanceIdAliases(input.componentInstanceId);
    const solverInput = this.createCommittedSolverInput(componentAliases, {
      transientMateDefinition: input.transientMateDefinition ?? null,
    });
    if (!solverInput) {
      return null;
    }

    for (const componentInstanceId of componentAliases) {
      if (!solverInput.components[componentInstanceId]) {
        continue;
      }
      const rigidGroup = resolveStandardMateFloatingRigidTransformGroup(solverInput, componentInstanceId);
      if (rigidGroup) {
        return rigidGroup;
      }
    }

    return null;
  }

  /**
   * 解析组件所在的“整个已提交配合连通装配”，用于整体刚性旋转。
   *
   * 设计说明：
   * 当选中零件的剩余自由度不支持鼠标方向旋转时，Rotate 需要“往上”把它所在的整个连通装配当作单一自由刚体
   * 整体旋转——整组做刚体运动会保持全部内部配合、并让剩余自由度的轴也一起转动，因此不会破坏任何配合。
   * 只有当连通装配“全浮动”（不含任何固定件）时才是可整体自由旋转的自由刚体；一旦含固定件，整组被锚住，
   * 返回 null 让上层回退到剩余轴约束旋转。连通性只看已提交配合，不掺入预览草稿。
   *
   * @param input 组件连通装配解析输入。
   * @returns 连通装配的全部组件实例 ID；无法整体自由旋转时返回 null。
   */
  public resolveFloatingConnectedTransformGroup(input: {
    readonly componentInstanceId: string;
  }): { readonly componentInstanceIds: readonly string[] } | null {
    const componentAliases = this.resolveComponentInstanceIdAliases(input.componentInstanceId);
    const solverInput = this.createCommittedSolverInput(componentAliases, {});
    if (!solverInput) {
      return null;
    }

    const componentInstanceIds = Object.keys(solverInput.components);
    if (componentInstanceIds.length === 0) {
      return null;
    }
    const hasFixedComponent = componentInstanceIds.some((componentInstanceId) => (
      solverInput.components[componentInstanceId]?.fixationState === 'fixed'
    ));
    if (hasFixedComponent) {
      return null;
    }

    return { componentInstanceIds };
  }

  /**
   * 解析组件实例 ID 的运行时别名集合。
   *
   * @param componentInstanceId 当前交互入口传入的组件 ID。
   * @returns 可用于匹配 Mate Definition 的 ID 集合。
   */
  private resolveComponentInstanceIdAliases(componentInstanceId: string): ReadonlySet<string> {
    const aliases = new Set<string>();
    const appendAlias = (value: string | undefined | null): void => {
      if (typeof value === 'string' && value.length > 0) {
        collectAssemblyNodeIdentityLookupIds(value).forEach((lookupId) => {
          aliases.add(lookupId);
        });
      }
    };
    appendAlias(componentInstanceId);

    const appendObjectAliases = (object: Object3D | null): void => {
      if (!object) {
        return;
      }

      const snapshot = createComponentInstanceSnapshotFromObject({ object });
      appendAlias(snapshot.componentInstanceId);
      appendAlias(snapshot.sourceNodeId);
      appendAlias(readAssemblyMateObjectAssemblyNodeId(object));
      appendAlias(object.uuid);
    };

    appendObjectAliases(this.runtime.resolveComponentObject(componentInstanceId));

    /**
     * 视口拖拽入口偶尔只能拿到 FeatureFace / FeatureLine / FeaturePoint 的运行时 uuid。
     * runtime 会拒绝把这些代理当作组件根，因此需要先从原始命中对象读取 assemblyNodeId，
     * 再用该稳定 ID 回到真实组件根，保证 committed mate 求解仍能把用户拖动侧当成锚点。
     */
    const rawObject = this.runtime.resolveSceneObjectById(componentInstanceId);
    appendObjectAliases(rawObject);

    const rawAssemblyNodeId = rawObject ? readAssemblyMateObjectAssemblyNodeId(rawObject) : '';
    if (rawAssemblyNodeId) {
      appendObjectAliases(this.runtime.resolveComponentObject(rawAssemblyNodeId));
    }

    return aliases;
  }

  /**
   * 创建文档恢复后的已提交 Mate 全量求解输入。
   *
   * 设计说明：
   * 1. `.puppy` 或项目模型子树恢复后，没有“用户正在拖动”的组件，不能借用移动命令的 driven 语义；
   * 2. 求解器已有 fixed / fallback 锚点策略，适合在恢复边界把当前文档收敛到可解状态；
   * 3. 引用缺失和不支持几何的 Mate 仍只保留诊断，不进入求解，避免旧 world-space 快照污染新模型。
   *
   * @returns 求解器输入；当前文档没有可求解 Mate 时返回 `null`。
   */
  private createDocumentRestoreSolverInput(): StandardMateSolverInput | null {
    const mateDocument = this.resolveMateDocument();
    const mateDefinitions = mateDocument.mates.filter((mate) => this.canUseCommittedMateForSolve(mate));
    const componentIds = new Set<string>();
    mateDefinitions.forEach((mate) => {
      resolveAssemblyMateComponentInstanceIds(mate).forEach((componentInstanceId) => {
        componentIds.add(componentInstanceId);
      });
    });
    if (mateDefinitions.length === 0 || componentIds.size === 0) {
      return null;
    }

    const components: Record<string, AssemblyMateSolverComponentState> = {};
    componentIds.forEach((componentInstanceId) => {
      const component = this.runtime.resolveComponentState(componentInstanceId);
      if (!component) {
        return;
      }

      components[componentInstanceId] = component;
    });

    return {
      components,
      mateDefinitions,
      transientMateIds: [],
      distanceBranchPolicy: StandardMateDistanceBranchPolicy.Continuous,
      useTransientInitialPlacement: false,
    };
  }

  /**
   * 创建已提交 Mate 的局部重求解输入。
   *
   * @param anchorComponentIds 本次交互驱动组件的 ID 与运行时别名。
   * @param options 局部求解输入选项。
   * @returns 求解器输入；没有相关 Mate 时返回 `null`。
   */
  private createCommittedSolverInput(
    anchorComponentIds: ReadonlySet<string>,
    options: CommittedSolverInputOptions = {},
  ): StandardMateSolverInput | null {
    const transientMate = options.transientMateDefinition ?? null;
    const transientComponentIds = transientMate
      ? new Set(resolveAssemblyMateComponentInstanceIds(transientMate))
      : new Set<string>();
    // 拖拽会联动到的已提交范围（不含瞬态）：用于判断瞬态“已装配侧”是否会被本次拖拽带动。
    const committedScopeComponentIds = options.expandTransientToCommittedScope
      ? this.collectCommittedScopeComponentIds(anchorComponentIds)
      : null;
    const shouldIncludeTransientMate = transientMate !== null
      && [...transientComponentIds].some((componentInstanceId) => (
        this.isComponentInstanceIdMatchedByAnyAnchor(componentInstanceId, anchorComponentIds)
        || this.isComponentInstanceIdInScope(componentInstanceId, committedScopeComponentIds)
      ));
    const scopeAnchorComponentIds = shouldIncludeTransientMate
      ? new Set([...anchorComponentIds, ...transientComponentIds])
      : anchorComponentIds;
    const scopedMates = this.resolveCommittedMateScope(scopeAnchorComponentIds);
    const mateDefinitions = shouldIncludeTransientMate
      ? [
          ...scopedMates.filter((mate) => mate.mateId !== transientMate.mateId),
          transientMate,
        ]
      : scopedMates;

    const componentIds = new Set<string>();
    mateDefinitions.forEach((mate) => {
      resolveAssemblyMateComponentInstanceIds(mate).forEach((componentInstanceId) => {
        componentIds.add(componentInstanceId);
      });
    });
    if (mateDefinitions.length === 0 || componentIds.size === 0) {
      return null;
    }

    const components: Record<string, AssemblyMateSolverComponentState> = {};
    componentIds.forEach((componentInstanceId) => {
      const component = this.runtime.resolveComponentState(componentInstanceId);
      if (!component) {
        return;
      }

      components[componentInstanceId] = component;
    });

    const drivenComponentInstanceIds = this.resolveDrivenCommittedComponentInstanceIds(
      anchorComponentIds,
      componentIds,
      components,
    );
    const drivenComponentConstraints = drivenComponentInstanceIds.reduce<
      Record<string, StandardMateDrivenComponentConstraint>
    >((constraints, componentInstanceId) => {
      const constraint = options.drivenComponentConstraints?.[componentInstanceId];
      return constraint
        ? {
            ...constraints,
            [componentInstanceId]: constraint,
          }
        : constraints;
    }, {});

    return {
      components,
      mateDefinitions,
      transientMateIds: shouldIncludeTransientMate && transientMate
        ? [transientMate.mateId]
        : [],
      drivenComponentInstanceIds,
      ...(Object.keys(drivenComponentConstraints).length > 0
        ? { drivenComponentConstraints }
        : {}),
      distanceBranchPolicy: StandardMateDistanceBranchPolicy.Continuous,
      useTransientInitialPlacement: false,
    };
  }

  /**
   * 将本次拖动入口身份映射回求解器实际使用的文档组件 ID。
   *
   * 设计说明：
   * 子装配离线包恢复到父工作区后，Mate Definition 会保存“父路径/局部节点”的完整身份；
   * 但移动命令仍可能只拿到局部 assemblyNodeId 或运行时对象 uuid。求解器的 driven 列表必须使用
   * `components` 中真实存在的文档组件 ID，否则求解器会把用户拖动侧当成普通 follower，表现为移动不受配合约束。
   *
   * @param anchorComponentIds 本次交互入口收集到的组件身份与运行时别名。
   * @param componentIds 当前 Mate 子图中的文档组件 ID。
   * @param components 已成功解析出的求解器组件状态。
   * @returns 求解器可消费的 driven 组件 ID 列表。
   */
  private resolveDrivenCommittedComponentInstanceIds(
    anchorComponentIds: ReadonlySet<string>,
    componentIds: ReadonlySet<string>,
    components: Readonly<Record<string, AssemblyMateSolverComponentState>>,
  ): string[] {
    return [...componentIds].filter((componentInstanceId) => (
      Boolean(components[componentInstanceId])
      && this.isComponentInstanceIdMatchedByAnyAnchor(componentInstanceId, anchorComponentIds)
    ));
  }

  /**
   * 解析与锚点组件连通的 Mate 子图。
   *
   * @param anchorComponentIds 驱动组件 ID 与运行时别名。
   * @returns 需要参与本次求解的 Mate 列表。
   */
  private resolveCommittedMateScope(
    anchorComponentIds: ReadonlySet<string>,
  ): readonly AssemblyMateDefinition[] {
    const mateDocument = this.resolveMateDocument();
    const adjacencyIndex = this.resolveCommittedMateAdjacencyIndex(mateDocument);
    const queue = [...anchorComponentIds];
    const visitedComponentIds = new Set(anchorComponentIds);
    const visitedMateIds = new Set<string>();
    const scopedMates: AssemblyMateDefinition[] = [];
    let queueIndex = 0;

    while (queueIndex < queue.length) {
      const componentInstanceId = queue[queueIndex];
      queueIndex += 1;
      if (!componentInstanceId) {
        continue;
      }

      const mateIds = this.resolveCommittedMateIdsForComponentId(adjacencyIndex, componentInstanceId);
      mateIds.forEach((mateId) => {
        if (visitedMateIds.has(mateId)) {
          return;
        }
        const mate = adjacencyIndex.mateById.get(mateId);
        if (!mate) {
          return;
        }
        visitedMateIds.add(mateId);
        scopedMates.push(mate);

        resolveAssemblyMateComponentInstanceIds(mate).forEach((nextComponentInstanceId) => {
          if (visitedComponentIds.has(nextComponentInstanceId)) {
            return;
          }
          visitedComponentIds.add(nextComponentInstanceId);
          queue.push(nextComponentInstanceId);
        });
      });
    }

    return scopedMates;
  }

  /**
   * 按组件身份解析已提交 Mate 邻接边。
   *
   * 设计说明：
   * 邻接表的 key 来自 Mate 文档，通常是恢复后的完整父路径；拖动入口的 key 来自场景对象，
   * 可能只包含离线包内部局部 ID。精确命中失败时，必须复用装配节点统一等价规则继续查找；
   * 同时用运行时 transform root 做二次校验，避免不同子装配里同名局部节点互相串配。
   *
   * @param adjacencyIndex 当前 Mate 文档的邻接索引。
   * @param componentInstanceId 待查询的运行时组件身份或别名。
   * @returns 与该组件相连的 Mate ID 列表。
   */
  private resolveCommittedMateIdsForComponentId(
    adjacencyIndex: CommittedMateAdjacencyIndex,
    componentInstanceId: string,
  ): readonly string[] {
    const exactMateIds = adjacencyIndex.mateIdsByComponentId.get(componentInstanceId);
    if (exactMateIds) {
      return exactMateIds;
    }

    const mateIds = new Set<string>();
    adjacencyIndex.mateIdsByComponentId.forEach((candidateMateIds, indexedComponentInstanceId) => {
      if (!this.isComponentInstanceIdMatchedByAnchor(indexedComponentInstanceId, componentInstanceId)) {
        return;
      }
      candidateMateIds.forEach((mateId) => {
        mateIds.add(mateId);
      });
    });
    return [...mateIds];
  }

  /**
   * 判断文档组件 ID 是否可由任一拖动入口身份代表。
   *
   * @param componentInstanceId Mate 文档中的组件 ID。
   * @param anchorComponentIds 本次交互入口收集到的组件身份与运行时别名。
   * @returns 任一入口身份可稳定代表该文档组件时返回 true。
   */
  private isComponentInstanceIdMatchedByAnyAnchor(
    componentInstanceId: string,
    anchorComponentIds: ReadonlySet<string>,
  ): boolean {
    return [...anchorComponentIds].some((anchorComponentId) => (
      this.isComponentInstanceIdMatchedByAnchor(componentInstanceId, anchorComponentId)
    ));
  }

  /**
   * 收集从拖拽锚点出发、经已提交配合连通的组件 ID（拖拽会联动到的整片零件）。
   *
   * @param anchorComponentIds 拖拽锚点组件 ID 与运行时别名。
   * @returns 已提交范围内的组件 ID 集合（含锚点自身）。
   */
  private collectCommittedScopeComponentIds(anchorComponentIds: ReadonlySet<string>): Set<string> {
    const componentIds = new Set<string>(anchorComponentIds);
    this.resolveCommittedMateScope(anchorComponentIds).forEach((mate) => {
      resolveAssemblyMateComponentInstanceIds(mate).forEach((componentInstanceId) => {
        componentIds.add(componentInstanceId);
      });
    });
    return componentIds;
  }

  /**
   * 判断组件是否落在拖拽会联动到的已提交范围内。
   *
   * 说明：瞬态配合与已提交配合都以 Mate 文档组件身份表达，直接判等即可；同时兼容子装配路径下的等价身份。
   *
   * @param componentInstanceId 瞬态配合一侧的组件 ID。
   * @param committedScopeComponentIds 已提交范围组件 ID 集合；未开启扩展时为 null。
   * @returns 是否落在已提交范围内。
   */
  private isComponentInstanceIdInScope(
    componentInstanceId: string,
    committedScopeComponentIds: ReadonlySet<string> | null,
  ): boolean {
    if (!committedScopeComponentIds) {
      return false;
    }
    if (committedScopeComponentIds.has(componentInstanceId)) {
      return true;
    }
    return [...committedScopeComponentIds].some((scopeComponentId) => (
      areAssemblyNodeIdentityEquivalent(scopeComponentId, componentInstanceId)
    ));
  }

  /**
   * 判断单个拖动入口身份是否可代表 Mate 文档组件。
   *
   * @param componentInstanceId Mate 文档中的组件 ID。
   * @param anchorComponentId 本次交互入口传入的组件 ID、局部 assemblyNodeId 或运行时 uuid。
   * @returns 两者指向同一 transform root 时返回 true。
   */
  private isComponentInstanceIdMatchedByAnchor(
    componentInstanceId: string,
    anchorComponentId: string,
  ): boolean {
    if (componentInstanceId === anchorComponentId) {
      return true;
    }
    if (!areAssemblyNodeIdentityEquivalent(componentInstanceId, anchorComponentId)) {
      return false;
    }

    const anchorObject = this.runtime.resolveComponentObject(anchorComponentId)
      ?? this.runtime.resolveSceneObjectById(anchorComponentId);
    const componentObject = this.runtime.resolveComponentObject(componentInstanceId);
    /**
     * 等价身份可能来自历史离线包路径，只有双方都解析到不同 transform root 时才判定为冲突。
     * 某些临时 Mate 或文档恢复边界尚未建立完整运行时对象，仍允许交给后续组件解析阶段生成诊断。
     */
    return !anchorObject || !componentObject || anchorObject === componentObject;
  }

  /**
   * 读取当前文档的已提交 Mate 邻接索引。
   *
   * @param mateDocument 当前 Mate 文档。
   * @returns 组件到 Mate 的邻接索引。
   */
  private resolveCommittedMateAdjacencyIndex(
    mateDocument: AssemblyMateDocumentModel,
  ): CommittedMateAdjacencyIndex {
    if (this.committedMateAdjacencyIndex?.document === mateDocument) {
      return this.committedMateAdjacencyIndex;
    }

    const mateById = new Map<string, AssemblyMateDefinition>();
    const mutableMateIdsByComponentId = new Map<string, string[]>();
    mateDocument.mates.forEach((mate) => {
      if (!this.canUseCommittedMateForSolve(mate)) {
        return;
      }

      mateById.set(mate.mateId, mate);
      resolveAssemblyMateComponentInstanceIds(mate).forEach((componentInstanceId) => {
        const mateIds = mutableMateIdsByComponentId.get(componentInstanceId) ?? [];
        mateIds.push(mate.mateId);
        mutableMateIdsByComponentId.set(componentInstanceId, mateIds);
      });
    });

    this.committedMateAdjacencyIndex = {
      document: mateDocument,
      mateById,
      mateIdsByComponentId: new Map(mutableMateIdsByComponentId),
    };
    return this.committedMateAdjacencyIndex;
  }

  /**
   * 判断历史 Mate 是否允许进入已提交 Mate 的实时求解子图。
   *
   * @param mate 历史 Mate 定义。
   * @returns 是否可参与本次实时求解。
   */
  private canUseCommittedMateForSolve(mate: AssemblyMateDefinition): boolean {
    if (mate.suppressed) {
      return false;
    }

    /**
     * 引用恢复失败或几何不支持时，Mate 文档只保留诊断上下文。
     * 这类 Mate 不能继续用旧 world-space 几何参与拖动求解，
     * 否则 `.puppy` 重载后首次移动会把组件拉回旧快照位置。
     */
    return mate.solveStatus !== MateSolveStatus.MissingReference
      && mate.solveStatus !== MateSolveStatus.UnsupportedGeometry;
  }

  /**
   * 将已提交 Mate 的重求解诊断写回文档。
   *
   * @param result 重求解结果。
   * @param transientMateIds 本次求解中的临时 Mate ID 集合。
   */
  private applyCommittedSolveDiagnostics(
    result: AssemblyMatePreviewResult,
    transientMateIds: ReadonlySet<string>,
  ): void {
    if (result.diagnostics.length === 0) {
      return;
    }

    let nextDocument = this.resolveMateDocument();
    const mateById = new Map(nextDocument.mates.map((mate) => [mate.mateId, mate]));
    let changed = false;
    result.diagnostics.forEach((diagnostic) => {
      if (transientMateIds.has(diagnostic.mateId)) {
        /**
         * 拖动释放阶段会要求写回诊断，但 PropertyManager 草稿 Mate 尚未进入 Mate 文档。
         * 临时 Mate 只限制当前自由度，不能调用文档编辑命令，否则会打断拖动收尾。
         */
        return;
      }

      const currentMate = mateById.get(diagnostic.mateId);
      if (!currentMate) {
        throw new Error(`无法写回装配体配合诊断，未找到已提交 Mate：${diagnostic.mateId}`);
      }
      const nextMessage = diagnostic.status === MateSolveStatus.Solved
        ? undefined
        : diagnostic.message;
      const currentMessage = currentMate?.errorMessage ?? undefined;
      if (currentMate?.solveStatus === diagnostic.status && currentMessage === nextMessage) {
        return;
      }

      changed = true;
      nextDocument = setAssemblyMateSolveStatus(
        nextDocument,
        diagnostic.mateId,
        diagnostic.status,
        nextMessage,
      );
    });
    if (!changed) {
      return;
    }

    this.commitMateDocument(nextDocument, {
      source: 'diagnostics',
      recordHistory: false,
    });
  }

  /**
   * 判断已提交 Mate 的实时求解诊断是否需要写回文档。
   *
   * @param result 已提交 Mate 的重求解结果。
   * @param transientMateIds 本次求解中的临时 Mate ID 集合。
   * @returns 是否需要写回 Mate 文档状态。
   */
  private shouldWriteCommittedSolveDiagnostics(
    result: AssemblyMatePreviewResult,
    transientMateIds: ReadonlySet<string>,
  ): boolean {
    const committedDiagnostics = result.diagnostics.filter((diagnostic) => (
      !transientMateIds.has(diagnostic.mateId)
    ));
    if (committedDiagnostics.length === 0) {
      return false;
    }
    if (result.status !== MateSolveStatus.Solved) {
      return true;
    }
    const diagnosticMateIds = new Set(committedDiagnostics.map((diagnostic) => diagnostic.mateId));
    return this.resolveMateDocument().mates.some((mate) => (
      diagnosticMateIds.has(mate.mateId)
      && (mate.solveStatus !== MateSolveStatus.Solved || Boolean(mate.errorMessage))
    ));
  }
}
